import { useState } from 'react'
import { useBuyContext } from '../../../state-management/contexts/BuyContext'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { AddUPIPage } from '../AddUPIPage'
import { useMutation } from '@tanstack/react-query'
import {
  InvestPaymentMethodType,
  InvestPaymentMethodTypes,
  PaymentPageType,
  UPISetupPayload,
  UPISetupResponse
} from '../../../types/mf-execution.types'
import { setupUPI } from '../../../repository/mfExecution'
import { useLoading } from '../../../state-management/contexts/LoadingContext'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { useNavigate } from 'react-router-dom'

export const BuyAddUpiPage = () => {
  const [isSetupError, setIsSetupError] = useState(false)
  const navigate = useNavigate()
  const { isin, data: metaData } = useMetaDataContext()
  const fundName = metaData?.fundDetails?.fundName ?? ''
  const trackingFund = isin && fundName ? { fundIsin: isin, fundName } : undefined
  const { showLoading, hideLoading } = useLoading()
  const { amount, paymentMethods, handleRefetchPaymentMethods, handlePaymentMethodChange, handleOTPTrigger } =
    useBuyContext()

  const navigateToOtp = () => {
    navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.BUY}/otp`)
  }

  const handleOrderOTPFlow = (data: UPISetupResponse) => {
    handleOTPTrigger({
      paymentMethodId: data?.paymentMethodId,
      paymentMethodType: InvestPaymentMethodTypes.UPI,
      transactionType: 'BUY',
      isin: isin,
      amount: amount
    })
    navigateToOtp()
  }

  const handleUpiSetupSuccess = (data: UPISetupResponse, payload: UPISetupPayload) => {
    handleRefetchPaymentMethods()
    const displayAccNumber = paymentMethods?.find(
      (method) => method?.accountNumber === payload?.accountNumber
    )?.displayAccNumber
    handlePaymentMethodChange({
      paymentMethodId: data?.paymentMethodId,
      type: InvestPaymentMethodTypes.UPI,
      displayAccNumber: displayAccNumber,
      upiId: payload?.upiId
    } as InvestPaymentMethodType)
  }

  const { mutate: setupUpiMutate } = useMutation({
    mutationFn: (payload: UPISetupPayload) => setupUPI(payload),
    onSuccess: (data: UPISetupResponse, variables: UPISetupPayload) => {
      if (data?.status === 'INVALID') {
        hideLoading()
        setIsSetupError(true)
        return
      }
      handleUpiSetupSuccess(data, variables) // Update the payment method in the context
      hideLoading()
      handleOrderOTPFlow(data) // Trigger the OTP flow
    },
    onError: () => {
      setIsSetupError(true)
      hideLoading()
    }
  })

  const handleSetupUpi = (payload: UPISetupPayload) => {
    showLoading()
    setupUpiMutate(payload)
  }

  return (
    <AddUPIPage
      trackingFund={trackingFund}
      bankAccounts={
        paymentMethods
          ?.filter((method) => method?.type === InvestPaymentMethodTypes.NET_BANKING)
          ?.map((account) => {
            return {
              logoUrl: account?.logoUrl ?? '/images/mutual_fund/categories/banks_grayscale.svg',
              displayAccNumber: account?.displayAccNumber ?? '',
              accountNumber: account?.accountNumber ?? '',
              ifscCode: account?.ifscCode ?? '',
              paymentMethodId: account?.paymentMethodId ?? ''
            }
          }) ?? []
      }
      cta={{ text: `Pay ${roundOffAndFormatNumberWithCurrency(amount)}`, onClick: handleSetupUpi }}
      isSetupError={isSetupError}
    />
  )
}
